/**
 * W0 — Plan finder / new plan. The event-scoped entry point to Oxbo-W: find a
 * well + event (adapting to event_letter + is_conventional), see each event's
 * plan status, and start / clone / version a plan or open the workspace. Built
 * to w0-plan-finder.html on the P3 kit.
 *
 * Data reactivity: each WellRow owns its `getWellEvents` query and each event's
 * EventChip owns its `getEventPlans` query, so the table fills in progressively
 * as the register → events → plan status stream in (no imperative cache reads).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@app/hooks";
import { selectWell, setActiveEvent, setActivePlan } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { useGetWellsQuery } from "@api/mApi";
import { useGetWellEventsQuery, useGetEventPlansQuery } from "@api/pApi";
import type { WellView } from "@api/mappers";
import type { EventOut } from "@api/types";
import { QueryState } from "@components/ui/QueryState";
import type { MessageKey } from "@i18n/messages";
import { EVENT_LETTER_OP_VAR, enrichEventCode, type EventLetter } from "@lib/eventEnrichment";
import {
  EMPTY_FILTERS,
  eventPasses,
  wellPasses,
  hasActiveFilters,
  distinctValues,
  type FinderFilters,
} from "./filterModel";
import { PlanStatusPill } from "./planPresentation";
import { latestPlan } from "./planCatalog";
import { NewPlanWizard } from "./NewPlanWizard";
import "./planFinder.css";

/** The event-type letters offered as quick filters, with their i18n label key. */
const LETTER_CHIPS: { letter: Extract<EventLetter, "P" | "T" | "W" | "L" | "S">; labelKey: MessageKey }[] = [
  { letter: "P", labelKey: "letter.P" },
  { letter: "T", labelKey: "letter.T" },
  { letter: "W", labelKey: "letter.W" },
  { letter: "L", labelKey: "letter.L" },
  { letter: "S", labelKey: "letter.S" },
];

export function PlanFinder() {
  const { t } = useLocale();
  const wellsQ = useGetWellsQuery();
  const wells = useMemo(() => wellsQ.data ?? [], [wellsQ.data]);

  const [filters, setFilters] = useState<FinderFilters>(EMPTY_FILTERS);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSeed, setWizardSeed] = useState<{
    well: WellView | null;
    letter: EventLetter | null;
    conv: boolean | null;
  }>({ well: null, letter: null, conv: null });

  const visibleWells = useMemo(() => wells.filter((w) => wellPasses(w, filters)), [wells, filters]);

  const basins = useMemo(() => distinctValues(wells, "basin"), [wells]);
  const fields = useMemo(() => distinctValues(wells, "field"), [wells]);
  const pads = useMemo(() => distinctValues(wells, "pad"), [wells]);

  function patch(p: Partial<FinderFilters>) {
    setFilters((f) => ({ ...f, ...p }));
  }
  function toggleLetter(l: EventLetter) {
    setFilters((f) => {
      const next = new Set(f.letters);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return { ...f, letters: next };
    });
  }
  function clearAll() {
    setFilters({ ...EMPTY_FILTERS, letters: new Set() });
  }

  function openNewPlan(seed?: { well: WellView | null; letter: EventLetter | null; conv: boolean | null }) {
    setWizardSeed(seed ?? { well: null, letter: null, conv: null });
    setWizardOpen(true);
  }

  const active = hasActiveFilters(filters);

  return (
    <div>
      <div className="screen-head">
        <h1>{t("finder.title")}</h1>
        <span className="badge info">{t("finder.badge")}</span>
        <div className="actions">
          <button type="button" className="btn primary" onClick={() => openNewPlan()}>
            <span className="ic ic-add" aria-hidden="true" /> {t("finder.newPlan")}
          </button>
        </div>
      </div>

      <div className="dim" style={{ fontSize: 12.5, margin: "-10px 0 16px" }}>
        {t("finder.lede")}
      </div>

      {/* Filter bar */}
      <section className="card" style={{ padding: "14px 16px 16px", marginBottom: 14 }}>
        <div className="finder-bar">
          <div className="field">
            <label htmlFor="fBasin">{t("finder.filter.basin")}</label>
            <select id="fBasin" value={filters.basin} onChange={(e) => patch({ basin: e.target.value })}>
              <option value="">{t("finder.filter.allBasin")}</option>
              {basins.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="fField">{t("finder.filter.field")}</label>
            <select id="fField" value={filters.field} onChange={(e) => patch({ field: e.target.value })}>
              <option value="">{t("finder.filter.allField")}</option>
              {fields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="fPad">{t("finder.filter.pad")}</label>
            <select id="fPad" value={filters.pad} onChange={(e) => patch({ pad: e.target.value })}>
              <option value="">{t("finder.filter.allPad")}</option>
              {pads.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="field typeahead">
            <label htmlFor="fWell">{t("finder.filter.well")}</label>
            <span className="ic ic-search" aria-hidden="true" />
            <input
              type="search"
              id="fWell"
              placeholder={t("finder.filter.wellPlaceholder")}
              autoComplete="off"
              value={filters.query}
              onChange={(e) => patch({ query: e.target.value })}
            />
          </div>
          <div className="field">
            <label>&nbsp;</label>
            <button type="button" className="btn" onClick={clearAll}>
              <span className="ic ic-refresh" aria-hidden="true" /> {t("finder.clear")}
            </button>
          </div>
        </div>

        <div className="field" style={{ marginTop: 2 }}>
          <label
            style={{
              display: "block",
              color: "var(--dim)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: ".04em",
              marginBottom: 7,
            }}
          >
            {t("finder.filter.eventType")}
          </label>
          <div className="letter-filter" role="group" aria-label={t("finder.filter.eventType")}>
            {LETTER_CHIPS.map(({ letter, labelKey }) => (
              <button
                key={letter}
                type="button"
                className={`lchip${filters.letters.has(letter) ? " on" : ""}`}
                onClick={() => toggleLetter(letter)}
              >
                <span className="sw" style={{ background: `var(${EVENT_LETTER_OP_VAR[letter]})` }} />
                {letter} · {t(labelKey)}
              </button>
            ))}
            <span className="dim" style={{ alignSelf: "center", fontSize: 12, marginLeft: 2 }}>
              ·
            </span>
            <button
              type="button"
              className={`lchip${filters.conv === "conv" ? " on" : ""}`}
              onClick={() => patch({ conv: filters.conv === "conv" ? null : "conv" })}
            >
              <span className="sw" style={{ background: "var(--good)" }} />
              {t("conv.convention")}
            </button>
            <button
              type="button"
              className={`lchip${filters.conv === "unconv" ? " on" : ""}`}
              onClick={() => patch({ conv: filters.conv === "unconv" ? null : "unconv" })}
            >
              <span className="sw" style={{ background: "var(--warn)" }} />
              {t("conv.unconvention")}
            </button>
          </div>
        </div>
      </section>

      <div className="result-meta">
        <span>
          <span className="n">{visibleWells.length}</span> {t("finder.count.wells")}
        </span>
        {active && (
          <button type="button" className="clear-filters" onClick={clearAll}>
            {t("finder.clearAll")}
          </button>
        )}
      </div>

      <QueryState
        isLoading={wellsQ.isLoading}
        isError={wellsQ.isError}
        isEmpty={!wellsQ.isLoading && visibleWells.length === 0}
        emptyMessage={t("finder.empty")}
        onRetry={() => void wellsQ.refetch()}
      >
        <div className="table-wrap">
          <table className="dt">
            <thead>
              <tr>
                <th>{t("finder.col.well")}</th>
                <th>{t("finder.col.location")}</th>
                <th>{t("finder.col.pad")}</th>
                <th>{t("finder.col.events")}</th>
                <th>{t("finder.col.planStatus")}</th>
                <th>{t("finder.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visibleWells.map((w) => (
                <WellRow key={w.id} well={w} filters={filters} onNewPlan={openNewPlan} />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>

      <div className="dim" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.6 }}>
        {t("finder.source")}
      </div>

      <NewPlanWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        seedWell={wizardSeed.well}
        seedLetter={wizardSeed.letter}
        seedConv={wizardSeed.conv}
      />
    </div>
  );
}

/** One well row — owns the events query so the row fills in as events arrive. */
function WellRow({
  well,
  filters,
  onNewPlan,
}: {
  well: WellView;
  filters: FinderFilters;
  onNewPlan: (seed: { well: WellView; letter: EventLetter | null; conv: boolean | null }) => void;
}) {
  const { t } = useLocale();
  const { data: events = [] } = useGetWellEventsQuery(well.uwi);

  const shownEvents = events.filter((ev) => eventPasses(ev, filters));

  // If the letter/conv filter is active and this well has no matching event, drop it.
  const filterActiveOnEvents = filters.letters.size > 0 || filters.conv !== null;
  if (filterActiveOnEvents && shownEvents.length === 0) return null;

  return (
    <tr>
      <td>
        <div className="well-cell">
          <b>{well.commonName}</b>
          <span className="uwi">{well.uwi}</span>
        </div>
      </td>
      <td>
        <div className="loc-cell">
          <span className="b">{well.basin}</span>
          <span className="y">{well.field}</span>
        </div>
      </td>
      <td>{well.pad}</td>
      <td className="events-col">
        <div className="evt-chips">
          {shownEvents.length === 0 && <span className="dim">{t("common.na")}</span>}
          {shownEvents.map((ev) => (
            <EventChip key={ev.id} well={well} event={ev} />
          ))}
        </div>
      </td>
      <td>
        {shownEvents.length === 0 ? (
          <span className="dim">{t("common.na")}</span>
        ) : (
          shownEvents.map((ev) => <EventPlanStatus key={ev.id} event={ev} />)
        )}
      </td>
      <td className="actions-col">
        <div className="row-actions-inline">
          <button
            type="button"
            className="actlink"
            onClick={() => onNewPlan({ well, letter: null, conv: null })}
          >
            {t("finder.action.newPlan")}
          </button>
        </div>
      </td>
    </tr>
  );
}

/** An event chip — owns the plans query to color/label by latest plan status. */
function EventChip({ well, event }: { well: WellView; event: EventOut }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const en = enrichEventCode(event.event_code);
  const { data: plans = [] } = useGetEventPlansQuery(event.id);
  const plan = latestPlan(plans);
  const opVar = en.letter ? EVENT_LETTER_OP_VAR[en.letter] : "--surface-3";

  function open() {
    dispatch(selectWell(well.id));
    dispatch(setActiveEvent(event.id));
    if (plan) dispatch(setActivePlan(plan.id));
    navigate("/workspace");
  }

  const conv = en.isConventional ?? event.is_conventional;

  return (
    <button
      type="button"
      className="evtchip"
      title={`${en.name} · ${event.event_code}`}
      onClick={open}
    >
      <span className="letter" style={{ background: `var(${opVar})` }}>
        {en.letter ?? "?"}
      </span>
      <span className="info">
        <span className="code">
          {event.event_code}{" "}
          <span className={conv === false ? "conv no" : "conv"} style={{ fontSize: 10, color: conv === false ? "var(--warn)" : "var(--dim)" }}>
            {conv === false ? "No conv." : conv === true ? "Conv." : ""}
          </span>
        </span>
        <span>
          <PlanStatusPill plan={plan} locale={locale} />
        </span>
      </span>
    </button>
  );
}

/** Plan status pill for the dedicated status column. */
function EventPlanStatus({ event }: { event: EventOut }) {
  const { locale } = useLocale();
  const { data: plans = [] } = useGetEventPlansQuery(event.id);
  const plan = latestPlan(plans);
  return (
    <div style={{ marginBottom: 4 }}>
      <PlanStatusPill plan={plan} locale={locale} />
    </div>
  );
}
